// Free Account Modal controller: visual parity with trial-offer modal, login actions from login modal

import { getCurrentLanguage, loadTranslations, applyTranslationsToContainer, interpolate } from '../translations.js';
import { hydratePricingFromPlans } from '../utils/pricingPlans.js';

const loadCSS = (href) => new Promise((resolve) => {
  try {
    if (document.querySelector(`link[href="${href}"]`)) return resolve(true);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(true);
    link.onerror = () => resolve(false);
    document.head.appendChild(link);
  } catch {
    resolve(false);
  }
});

const loadTemplate = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
};

const modalElementFromHtml = (html) => {
  const host = document.createElement('div');
  host.innerHTML = html;
  return [...host.children].find((node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') ?? null;
};

async function loadModalTranslations(lang) {
  await Promise.all([
    loadTranslations('free-account-modal', lang),
    loadTranslations('login', lang),
    loadTranslations('account', lang),
    loadTranslations('pricing', lang),
  ]);
}

function applyModalTranslations(modal, lang) {
  if (!modal) return;
  applyTranslationsToContainer(modal, 'free-account-modal', lang);
  applyTranslationsToContainer(modal, 'login', lang);
  applyTranslationsToContainer(modal, 'pricing', lang);
}

function wireCloseBehavior(modal, onClose) {
  const closeModal = () => {
    modal.classList.remove('show');
    setTimeout(() => {
      modal.style.display = 'none';
      if (onClose) onClose();
    }, 300);
  };

  const closeBtn = modal.querySelector('.quota-pricing-close');
  closeBtn?.addEventListener('click', () => closeModal());
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  const onEsc = (e) => { if (e.key === 'Escape') { closeModal(); document.removeEventListener('keydown', onEsc); } };
  document.addEventListener('keydown', onEsc);
}

function wireAuthActions(modal) {
  let isProcessing = false;
  let lastRequestTime = 0;

  const showMessage = (text, type = 'error', position = 'upper') => {
    const selector = position === 'upper' ? '.auth-panel-message.success-message' : '.auth-panel-message.countdown-message';
    const el = modal.querySelector(selector);
    if (!el) return;
    el.textContent = text;
    el.style.display = text ? 'block' : 'none';
    el.className = `auth-panel-message ${position === 'upper' ? 'success-message' : 'countdown-message'} ${type}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    const now = Date.now();
    if (isProcessing || (now - lastRequestTime) < 60000) return;
    const emailInput = modal.querySelector('.auth-login-input');
    const email = emailInput?.value?.trim();
    if (!email) { showMessage('Email required'); return; }

    const btn = e.currentTarget;
    const originalText = btn.textContent;
    isProcessing = true;
    lastRequestTime = now;
    btn.disabled = true;
    btn.style.cursor = 'default';
    btn.textContent = 'Sending...';

    try {
      const { initOAuthManager } = await import('../utils/oauthManager.js');
      const oauthManager = initOAuthManager();
      await oauthManager.startMagicLinkAuth(email);
      const lang = getCurrentLanguage();
      const loginT = window.translations?.[lang]?.login;
      showMessage(loginT?.magicLinkSuccessMessage || 'Great! Check your email 📨', 'success', 'upper');
      if (emailInput) emailInput.value = '';
      btn.textContent = loginT?.sent || 'Sent!';

      // Start countdown after a brief delay to show success message
      setTimeout(() => startResendCountdown(btn, originalText), 7000);
    } catch (error) {
      const loginBundle = window.translations?.[getCurrentLanguage()]?.login;
      const loginT = loginBundle?.login || loginBundle;
      const resetSendButton = () => {
        isProcessing = false;
        lastRequestTime = 0;
        btn.disabled = false;
        btn.style.cursor = '';
        btn.textContent = loginT?.sendMagicLink || originalText;
      };

      if (error?.message === 'EMAIL_TYPO_EDIT_REQUESTED') {
        emailInput?.focus();
        emailInput?.select?.();
        resetSendButton();
        return;
      }

      const message = error?.message === 'Invalid email address'
        ? (loginT?.magicLinkInvalidEmail || 'Invalid email format. Use an address like name@example.com.')
        : (loginT?.magicLinkSendFailed || "Couldn't send the link. Try a different email, or continue with Google or Discord.");
      showMessage(message);
      resetSendButton();
    }
  };

  const handleSocial = (provider) => async () => {
    try {
      const { initOAuthManager } = await import('../utils/oauthManager.js');
      const oauthManager = initOAuthManager();
      const method = `start${provider[0].toUpperCase()}${provider.slice(1)}Auth`;
      await oauthManager[method]();
    } catch {
      showMessage(`${provider} auth failed`);
    }
  };

  const startResendCountdown = (btn, originalText) => {
    let remainingSeconds = 55;
    const updateCountdown = () => {
      if (remainingSeconds > 0) {
        const loginT = window.translations?.[getCurrentLanguage()]?.login;
        const message = interpolate(loginT?.magicLinkResendMessage || "Didn't receive the email? Check your spams, or try again in {seconds} seconds", { seconds: remainingSeconds });
        showMessage(message, 'info', 'lower');
        remainingSeconds--;
        setTimeout(updateCountdown, 1000);
      } else {
        showMessage('', 'info', 'lower');
        btn.disabled = false;
        btn.style.cursor = '';
        btn.textContent = window.translations?.[getCurrentLanguage()]?.login?.sendMagicLink || originalText;
        isProcessing = false;
      }
    };
    updateCountdown();
  };

  modal.querySelector('.auth-login-btn')?.addEventListener('click', handleMagicLink);
  modal.querySelector('.discord-login-btn')?.addEventListener('click', handleSocial('discord'));
  modal.querySelector('.google-login-btn')?.addEventListener('click', handleSocial('google'));
}

const applyFreeAccountCopy = (modal, { title, subtitle } = {}) => {
  if (title) {
    const titleEl = modal.querySelector('#freeAccountTitle, .trial-offer-title');
    if (titleEl) titleEl.textContent = title;
  }
  if (subtitle) {
    const subtitleEl = modal.querySelector('.trial-price');
    if (subtitleEl) subtitleEl.textContent = subtitle;
  }
};

export const showFreeAccountModal = async (_context = 'unknown', { title, subtitle } = {}) => {
  try {
    await Promise.all([
      loadCSS('/css/modals/pricing.css'),
      loadCSS('/css/modals/pricing-trial-auth.css')
    ]);

    const html = await loadTemplate('/components/modals/free-account-modal.html');
    if (!html) return console.error('Failed to load free account modal template');

    const modal = modalElementFromHtml(html);
    if (!modal) return console.error('Failed to parse free account modal template');

    const lang = getCurrentLanguage();
    await loadModalTranslations(lang);
    applyModalTranslations(modal, lang);
    await hydratePricingFromPlans(modal, lang);
    applyFreeAccountCopy(modal, { title, subtitle });

    document.body.appendChild(modal);
    wireCloseBehavior(modal);
    wireAuthActions(modal);

    try {
      const { initializeTrialImageGridFor } = await import('../utils/quotaErrorHandler.js');
      initializeTrialImageGridFor?.(modal);
    } catch {}

    requestAnimationFrame(() => {
      modal.style.display = 'flex';
      modal.classList.add('show');
    });

    let activeLang = lang;
    const onLangChange = async (e) => {
      const newLang = e?.detail?.language;
      if (!newLang || newLang === activeLang) return;
      activeLang = newLang;
      await loadModalTranslations(newLang);
      applyModalTranslations(modal, newLang);
      await hydratePricingFromPlans(modal, newLang);
      applyFreeAccountCopy(modal, { title, subtitle });
    };
    document.addEventListener('languageChanged', onLangChange);

    // Cleanup language listener when modal is removed
    const mo = new MutationObserver(() => {
      if (!document.body.contains(modal)) {
        try { document.removeEventListener('languageChanged', onLangChange); } catch {}
        mo.disconnect();
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {
    console.error('showFreeAccountModal error:', e);
  }
};

export default { showFreeAccountModal };


